import { useState } from "react";
import { useTellescopeQuery } from "@tellescope/tanstack-query";
import { useEnduserStore } from "../../store";
import { useJoinVideoCall } from "../../hooks/useJoinVideoCall";
import { isEventCancelledForEnduser } from "../../hooks/useEventStatus";
import { WAITING_ROOM_POLL_INTERVAL_MS } from "./useVideoWaitingRoom";

export type CallDisconnectStatus = "checking" | "dropped" | "ended";

/**
 * After the enduser's room disconnects, tells apart the provider ending the meeting from the enduser's own
 * connection dropping (network loss, or the call opened in another tab). Twilio reports both as a disconnect, so the
 * meeting status on the server decides. Keeps checking while the meeting is live, so a later end is still reflected.
 */
export const useCallDisconnectStatus = (calendarEventId: string) => {
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const { joinVideoCall } = useJoinVideoCall();

  const [disconnectedAt] = useState(() => Date.now());
  const { data, dataUpdatedAt, isError } = useTellescopeQuery({
    model: "calendar_events",
    method: "getOne",
    request: calendarEventId,
    staleTime: 0,
    refetchInterval: (query) => (query.state.data?.meetingStatus === "live" ? WAITING_ROOM_POLL_INTERVAL_MS : false),
  });

  // The provider ending the meeting disconnects the room just before the server records it, so cached data can't decide
  const event = dataUpdatedAt >= disconnectedAt ? data : undefined;

  // A failed check usually means the enduser is offline, i.e. their own connection dropped. Offering Rejoin is safe:
  // the waiting room re-confirms the meeting before connecting.
  const status: CallDisconnectStatus = !event
    ? isError
      ? "dropped"
      : "checking"
    : event.meetingStatus === "live" && !isEventCancelledForEnduser(event, currentEnduser)
    ? "dropped"
    : "ended";

  const rejoin = () => {
    const rejoinEvent = event ?? data;
    if (rejoinEvent) joinVideoCall(rejoinEvent);
  };

  return { status, rejoin };
};
